import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { normalizePath } from "@/lib/canonicalize";
import { BRAND_REGISTRY } from "@/lib/brandRegistry";

/**
 * Client-side URL normalizer. For every registered brand, redirects the apex
 * (and any legacy aliases) to the canonical www host so DomainSEO canonical
 * tags and served URL stay in lockstep.
 */
const UrlNormalizer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { pathname, search, hash, hostname, protocol } = window.location;
    const host = hostname.toLowerCase();

    // Stage 1 canonical-host repair (2026-07-12): OBC canonical is the apex
    // host `obriencontractingusa.com`. Redirect the legacy www alias and the
    // former obriencontractusa.com alias set to the apex canonical host.
    if (
      host === "www.obriencontractingusa.com" ||
      host === "obriencontractusa.com" ||
      host === "www.obriencontractusa.com"
    ) {
      window.location.replace(
        `${protocol}//obriencontractingusa.com${pathname}${search}${hash}`,
      );
      return;
    }

    // For every other brand: if served from a non-canonical host in the
    // hostnames alias list, hop to that brand's canonicalHost. OBC is handled
    // above so this loop only reshapes secondary brands (RCF, microsites)
    // whose canonicalHost may legitimately be a www. subdomain.
    for (const brand of BRAND_REGISTRY) {
      if (brand.brand === "obc") continue;
      if (host === brand.canonicalHost) break;
      if (brand.hostnames.includes(host)) {
        window.location.replace(
          `${protocol}//${brand.canonicalHost}${pathname}${search}${hash}`,
        );
        return;
      }
    }

    // Path normalization (slug dedupe, trailing slash, legacy aliases).
    const normalized = normalizePath(pathname);
    if (normalized !== pathname) {
      navigate(normalized + search + hash, { replace: true });
    }
  }, [location.pathname]);

  return null;
};

export default UrlNormalizer;
